/**
 * Pure domain logic for Rack Operational Lifecycle Statuses and Functional Roles.
 * Standard NetBox DCIM alignment.
 * 
 * Statuses: 'planned' | 'active' | 'reserved' | 'deprecated'
 * Roles: 'Server' | 'Network' | 'Storage' | 'Colocation' | 'Edge' | 'Infrastructure'
 */

export type RackLifecycleStatus = 'planned' | 'active' | 'reserved' | 'deprecated';

export type RackRole = 'Server' | 'Network' | 'Storage' | 'Colocation' | 'Edge' | 'Infrastructure';

export interface RackStatusMeta {
  status: RackLifecycleStatus;
  labelRu: string;
  labelEn: string;
  color: string;
  badgeBg: string;
  badgeBorder: string;
  description: string;
  canvasStrokeDash?: number[];
  canvasOpacity: number;
  canvasBadge?: string;
}

export interface RackRoleMeta {
  role: RackRole;
  labelRu: string;
  labelEn: string;
  color: string;
  badgeBg: string;
  badgeBorder: string;
  description: string;
  shortCode: string;
}

export const RACK_STATUS_METAS: Record<RackLifecycleStatus, RackStatusMeta> = {
  active: {
    status: 'active',
    labelRu: 'Активна',
    labelEn: 'Active',
    color: '#10b981',
    badgeBg: 'rgba(16, 185, 129, 0.15)',
    badgeBorder: 'rgba(16, 185, 129, 0.4)',
    description: 'В промышленной эксплуатации (Production)',
    canvasOpacity: 1.0,
  },
  planned: {
    status: 'planned',
    labelRu: 'Запланирована',
    labelEn: 'Planned',
    color: '#38bdf8',
    badgeBg: 'rgba(56, 189, 248, 0.15)',
    badgeBorder: 'rgba(56, 189, 248, 0.4)',
    description: 'Планируемое расширение зала / монтаж',
    canvasStrokeDash: [6, 4],
    canvasOpacity: 0.55,
    canvasBadge: 'PLAN',
  },
  reserved: {
    status: 'reserved',
    labelRu: 'Зарезервирована',
    labelEn: 'Reserved',
    color: '#a855f7',
    badgeBg: 'rgba(168, 85, 247, 0.15)',
    badgeBorder: 'rgba(168, 85, 247, 0.4)',
    description: 'Зарезервирована под заказчика или проект',
    canvasStrokeDash: [4, 4],
    canvasOpacity: 0.85,
    canvasBadge: 'RES',
  },
  deprecated: {
    status: 'deprecated',
    labelRu: 'Выводится',
    labelEn: 'Deprecated',
    color: '#f59e0b',
    badgeBg: 'rgba(245, 158, 11, 0.15)',
    badgeBorder: 'rgba(245, 158, 11, 0.5)',
    description: 'Вывод из эксплуатации (Decommissioning)',
    canvasOpacity: 0.9,
    canvasBadge: 'DEP',
  },
};

export const RACK_ROLE_METAS: Record<RackRole, RackRoleMeta> = {
  Server: {
    role: 'Server',
    labelRu: 'Серверная',
    labelEn: 'Server',
    color: '#3b82f6',
    badgeBg: 'rgba(59, 130, 246, 0.15)',
    badgeBorder: 'rgba(59, 130, 246, 0.4)',
    description: 'Вычислительные узлы и серверы общего назначения',
    shortCode: 'SRV',
  },
  Network: {
    role: 'Network',
    labelRu: 'Сетевая',
    labelEn: 'Network',
    color: '#22d3ee',
    badgeBg: 'rgba(34, 211, 238, 0.15)',
    badgeBorder: 'rgba(34, 211, 238, 0.4)',
    description: 'Маршрутизаторы, коммутаторы ядра/агрегации/ToR',
    shortCode: 'NET',
  },
  Storage: {
    role: 'Storage',
    labelRu: 'Хранение',
    labelEn: 'Storage',
    color: '#818cf8',
    badgeBg: 'rgba(129, 140, 248, 0.15)',
    badgeBorder: 'rgba(129, 140, 248, 0.4)',
    description: 'СХД, SAN-коммутаторы, полки дисков JBOD/NVMe',
    shortCode: 'SAN',
  },
  Colocation: {
    role: 'Colocation',
    labelRu: 'Colocation',
    labelEn: 'Colocation',
    color: '#a855f7',
    badgeBg: 'rgba(168, 85, 247, 0.15)',
    badgeBorder: 'rgba(168, 85, 247, 0.4)',
    description: 'Клиентские стойки под аренду машиномест',
    shortCode: 'COLO',
  },
  Edge: {
    role: 'Edge',
    labelRu: 'Edge',
    labelEn: 'Edge',
    color: '#14b8a6',
    badgeBg: 'rgba(20, 184, 166, 0.15)',
    badgeBorder: 'rgba(20, 184, 166, 0.4)',
    description: 'Пограничные узлы связи и распределенные точки присутствия',
    shortCode: 'EDGE',
  },
  Infrastructure: {
    role: 'Infrastructure',
    labelRu: 'Инфраструктура',
    labelEn: 'Infrastructure',
    color: '#f97316',
    badgeBg: 'rgba(249, 115, 22, 0.15)',
    badgeBorder: 'rgba(249, 115, 22, 0.4)',
    description: 'Инженерное оборудование (ИБП, PDU, кондиционеры)',
    shortCode: 'INFRA',
  },
};

export const ALL_RACK_STATUSES: RackLifecycleStatus[] = ['active', 'planned', 'reserved', 'deprecated'];
export const ALL_RACK_ROLES: RackRole[] = ['Server', 'Network', 'Storage', 'Colocation', 'Edge', 'Infrastructure'];

/**
 * Normalizes raw status string to a valid RackLifecycleStatus.
 * Backward compatible: defaults to 'active' if missing or unknown.
 */
export function normalizeRackStatus(raw?: string | null): RackLifecycleStatus {
  if (!raw) return 'active';
  const clean = raw.trim().toLowerCase();
  if (clean === 'planned' || clean === 'plan') return 'planned';
  if (clean === 'reserved' || clean === 'reserve') return 'reserved';
  if (clean === 'deprecated' || clean === 'decommissioned' || clean === 'decom') return 'deprecated';
  if (clean === 'active' || clean === 'production' || clean === 'prod') return 'active';
  return 'active';
}

/**
 * Normalizes raw role or derives an intelligent default from rackType.
 */
export function normalizeRackRole(rawRole?: string | null, rackType?: string | null): RackRole {
  if (rawRole) {
    const clean = rawRole.trim().toLowerCase();
    for (const r of ALL_RACK_ROLES) {
      if (r.toLowerCase() === clean) return r;
    }
    if (clean === 'san' || clean === 'storage') return 'Storage';
    if (clean === 'net' || clean === 'network') return 'Network';
    if (clean === 'colo' || clean === 'colocation') return 'Colocation';
    if (clean === 'infra' || clean === 'facility') return 'Infrastructure';
  }

  // Derive from rackType if role is unset
  if (rackType) {
    const rt = rackType.toLowerCase();
    if (rt === 'network_rack') return 'Network';
    if (rt === 'crac' || rt === 'inrow_cooler' || rt === 'ups' || rt === 'pdu_frame' || rt === 'pillar') {
      return 'Infrastructure';
    }
  }

  return 'Server';
}

/**
 * Computes visual canvas parameters for a rack on 2D Konva.
 */
export interface CanvasBadgeStyle {
  text: string;
  bg: string;
  border: string;
  color: string;
}

export interface RackCanvasVisuals {
  status: RackLifecycleStatus;
  role: RackRole;
  opacity: number;
  strokeDash?: number[];
  strokeColor?: string;
  badge?: string;
  badgeStyle?: CanvasBadgeStyle;
  hasHatchOverlay: boolean;
  shortCode: string;
  isPlanned: boolean;
  isDeprecated: boolean;
}

export function getRackCanvasVisuals(
  rack: {
    status?: string | null;
    role?: string | null;
    rackType?: string | null;
    isBlocked?: boolean;
    isFacility?: boolean;
  },
  _defaultAccent?: string
): RackCanvasVisuals {
  const status = normalizeRackStatus(rack.status);
  const role = normalizeRackRole(rack.role, rack.rackType);
  const meta = RACK_STATUS_METAS[status];
  const roleMeta = RACK_ROLE_METAS[role];

  const strokeDash = meta.canvasStrokeDash;
  const opacity = meta.canvasOpacity;
  let strokeColor: string | undefined = undefined;
  const badge = meta.canvasBadge;
  let badgeStyle: CanvasBadgeStyle | undefined = undefined;

  if (status === 'deprecated') {
    strokeColor = '#f59e0b'; // Amber warning border
    badgeStyle = { text: 'DEP', bg: '#78350f', border: '#f59e0b', color: '#fbbf24' };
  } else if (status === 'planned') {
    strokeColor = '#38bdf8'; // Sky blue dashed border
    badgeStyle = { text: 'PLAN', bg: '#0c4a6e', border: '#38bdf8', color: '#7dd3fc' };
  } else if (status === 'reserved') {
    strokeColor = '#a855f7'; // Purple reservation border
    badgeStyle = { text: 'RES', bg: '#581c87', border: '#a855f7', color: '#e9d5ff' };
  }

  return {
    status,
    role,
    opacity,
    strokeDash,
    strokeColor,
    badge,
    badgeStyle,
    hasHatchOverlay: status === 'reserved',
    shortCode: roleMeta.shortCode,
    isPlanned: status === 'planned',
    isDeprecated: status === 'deprecated',
  };
}

/**
 * Filters a list of racks by query, matching label, row, type, status, and role.
 */
export function filterRacksWithLifecycle(
  racks: any[],
  query: string,
  filterStatus?: string | null,
  filterRole?: string | null
): any[] {
  const q = (query || '').trim().toLowerCase();
  const fStatus = filterStatus ? filterStatus.trim().toLowerCase() : null;
  const fRole = filterRole ? filterRole.trim().toLowerCase() : null;

  return racks.filter(r => {
    const status = normalizeRackStatus(r.status);
    const role = normalizeRackRole(r.role, r.rackType);

    if (fStatus && fStatus !== 'all' && status !== fStatus) {
      return false;
    }

    if (fRole && fRole !== 'all' && role.toLowerCase() !== fRole) {
      return false;
    }

    if (!q) return true;

    const labelMatch = (r.label || '').toLowerCase().includes(q);
    const rowMatch = (r.rowId || '').toLowerCase().includes(q);
    const idMatch = (r.id || '').toLowerCase().includes(q);
    const typeMatch = (r.rackType || r.type || '').toLowerCase().includes(q);
    const statusMatch = status.includes(q) || RACK_STATUS_METAS[status].labelRu.toLowerCase().includes(q);
    const roleMatch = role.toLowerCase().includes(q) || RACK_ROLE_METAS[role].labelRu.toLowerCase().includes(q);

    return labelMatch || rowMatch || idMatch || typeMatch || statusMatch || roleMatch;
  });
}

/**
 * Batch updates lifecycle status and/or role for a set of racks.
 */
export function batchUpdateRackLifecycle(
  racks: Record<string, any>,
  targetIds: string[],
  updates: { status?: RackLifecycleStatus; role?: RackRole }
): { updatedRacks: Record<string, any>; changedCount: number } {
  const nextRacks = { ...racks };
  let changedCount = 0;

  for (const id of targetIds) {
    const r = nextRacks[id];
    if (!r) continue;
    let modified = false;
    const newRack = { ...r };

    if (updates.status && newRack.status !== updates.status) {
      newRack.status = updates.status;
      modified = true;
    }
    if (updates.role && newRack.role !== updates.role) {
      newRack.role = updates.role;
      modified = true;
    }

    if (modified) {
      newRack.isDirty = true;
      nextRacks[id] = newRack;
      changedCount++;
    }
  }

  return { updatedRacks: nextRacks, changedCount };
}
